/**
 * Panel lateral derecho, NO modal: muestra contexto sin sacar de la pantalla.
 *
 * Se anuncia como `role="dialog"` sin `aria-modal`: tiene título, se cierra con
 * Escape y recibe el foco al abrir, pero lo que queda detrás sigue siendo usable
 * (el grafo se puede recorrer con el panel abierto). No hay fondo que bloquee
 * ni trampa de foco.
 *
 * Foco:
 * - Al abrir va al título, que así se anuncia primero.
 * - Al cerrar vuelve a `returnFocusRef` (el botón que lo abrió) o, si no se
 *   pasó, a lo que tenía el foco al abrir. Solo si el foco estaba en el panel
 *   (o en ninguna parte): quien ya se fue a otro control no lo pierde.
 * - Escape lo cierra cuando viene del panel o de ninguna parte. Si el foco está
 *   en otro control, la tecla es de ese control (una caja del grafo la usa para
 *   soltar su selección).
 *
 * SE DIBUJA EN UN PORTAL SOBRE <body>. Es `position: fixed` contra la ventana, y
 * un ancestro con `transform`, `filter`, `contain` o `container-type` (un marco
 * que use container queries, por ejemplo) lo anclaría a ese ancestro en vez de
 * a la ventana. Desde <body> no depende de cómo arme la página el marco. En el
 * árbol de React sigue donde se lo usa: contexto y eventos funcionan igual.
 *
 * Cómo se acopla a la página: ver `styles/side-panel.css`.
 */
import { useEffect, useId, useLayoutEffect, useRef } from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { IconClose } from './icons';
import '../styles/side-panel.css';

export interface SidePanelProps {
  /** Título del panel. Es también su nombre accesible. */
  readonly title: ReactNode;
  /** Bajada bajo el título; se anuncia como la descripción del panel. */
  readonly subtitle?: ReactNode | undefined;
  /** Pide cerrar el panel: su botón de cierre o Escape. */
  readonly onClose: () => void;
  /** Acciones al pie (enlaces o botones). Sin pie si no se pasa. */
  readonly footer?: ReactNode | undefined;
  /** A dónde vuelve el foco al cerrar; normalmente el botón que lo abrió. */
  readonly returnFocusRef?: RefObject<HTMLElement | null> | undefined;
  /** Id del panel, para el `aria-controls` de quien lo abre. */
  readonly id?: string | undefined;
  readonly children: ReactNode;
}

export function SidePanel({
  title,
  subtitle,
  onClose,
  footer,
  returnFocusRef,
  id,
  children,
}: SidePanelProps): ReactElement {
  const titleId = useId();
  const subtitleId = useId();
  const panelRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Lo último que pasó el padre, leído desde los efectos sin volver a
  // suscribirlos en cada render: el padre suele pasar funciones nuevas.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const returnFocusTarget = useRef(returnFocusRef);
  returnFocusTarget.current = returnFocusRef;

  // Efecto de LAYOUT a propósito: su limpieza corre antes de que React saque el
  // panel del DOM, así todavía se puede saber si el foco estaba adentro. Con un
  // efecto normal el panel ya no estaría y el foco habría caído en <body>.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    titleRef.current?.focus({ preventScroll: true });

    return () => {
      const active = document.activeElement;
      const focusWasInPanel =
        active === null || active === document.body || (panel?.contains(active) ?? false);
      if (!focusWasInPanel) {
        return;
      }
      const target = returnFocusTarget.current?.current ?? previous;
      if (target?.isConnected) {
        target.focus({ preventScroll: true });
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) {
        return;
      }
      const target = event.target;
      const fromPanel = target instanceof Node && (panelRef.current?.contains(target) ?? false);
      const fromNowhere = target === document.body || target === document.documentElement;
      if (fromPanel || fromNowhere) {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return createPortal(
    <section
      ref={panelRef}
      id={id}
      className="side-panel"
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={subtitle === undefined ? undefined : subtitleId}
    >
      <header className="side-panel__header">
        <div className="side-panel__heading">
          <h2 ref={titleRef} id={titleId} className="side-panel__title" tabIndex={-1}>
            {title}
          </h2>
          {subtitle === undefined ? null : (
            <p id={subtitleId} className="side-panel__subtitle">
              {subtitle}
            </p>
          )}
        </div>
        <button
          type="button"
          className="button button--ghost side-panel__close"
          aria-label="Cerrar panel"
          onClick={onClose}
        >
          <IconClose size={16} className="side-panel__close-icon" />
        </button>
      </header>

      {/*
        El cuerpo hace su propio scroll, así que tiene que poder recibir el foco:
        sin eso, quien usa teclado no puede recorrer un contenido sin enlaces.
      */}
      <div className="side-panel__body" role="region" aria-labelledby={titleId} tabIndex={0}>
        {children}
      </div>

      {footer === undefined ? null : <footer className="side-panel__footer">{footer}</footer>}
    </section>,
    document.body,
  );
}
